import React, { useState } from "react";
import RegistrationForm from "../components/forms/RegistrationForm.jsx";
import ErrorMessage from "../components/common/ErrorMessage.jsx";
import SuccessMessage from "../components/common/SuccessMessage.jsx";
import Logo from "../components/common/Logo.jsx";
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from "../utils/constants.js";

/**
 * Register Page Component
 *
 * Provides the complete registration page layout with:
 * - Registration form integration
 * - Success/error message handling
 * - Firebase service integration
 * - User-friendly interface
 *
 * Requirements: 1.2, 1.7
 */
const Register = () => {
  const [pageState, setPageState] = useState({
    isSubmitting: false,
    submitSuccess: false,
    submitError: null,
    registrationId: null,
  });

  /**
   * Handle successful form submission
   * @param {Object} registrationData - The submitted registration data with ID
   */
  const handleSubmissionSuccess = async (registrationData) => {
    try {
      setPageState((prev) => ({
        ...prev,
        submitSuccess: true,
        submitError: null,
        registrationId: registrationData.id,
      }));

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setPageState((prev) => ({
          ...prev,
          submitSuccess: false,
        }));
      }, 5000);
    } catch (error) {
      console.error("Error in post-submission handling:", error);
      setPageState((prev) => ({
        ...prev,
        submitError:
          "Registration submitted but there was an issue with follow-up processing.",
        submitSuccess: false,
      }));
    }
  };

  /**
   * Handle form submission errors
   * @param {Error} error - The error that occurred
   */
  const handleSubmissionError = (error) => {
    console.error("Registration submission error:", error);
    setPageState((prev) => ({
      ...prev,
      submitError: error.message || ERROR_MESSAGES.NETWORK_ERROR,
      submitSuccess: false,
    }));
  };

  /**
   * Clear page-level error messages
   */
  const clearPageError = () => {
    setPageState((prev) => ({
      ...prev,
      submitError: null,
    }));
  };

  return (
    <div className="register-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <Logo size="large" className="page-logo" />
          <h1 className="page-title">Mirt Sew</h1>
          <h2 className="page-title-2">User Registration</h2>
          <p className="page-description">
            Please fill out all required information to submit your registration
            application. You will receive an email notification once your
            application has been reviewed.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        <div className="container">
          {/* Page-level Success Message */}
          {pageState.submitSuccess && (
            <div className="page-message success-section">
              <SuccessMessage
                message={SUCCESS_MESSAGES.REGISTRATION_SUBMITTED}
                className="page-success"
              />
              {pageState.registrationId && (
                <div className="registration-details">
                  <p className="registration-id">
                    <strong>Registration ID:</strong> {pageState.registrationId}
                  </p>
                  <p className="next-steps">
                    Please save this ID for your records. You can use it to
                    reference your application if needed.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Page-level Error Message */}
          {pageState.submitError && (
            <div className="page-message error-section">
              <ErrorMessage
                message={pageState.submitError}
                className="page-error"
              />
              <button
                className="error-dismiss-button"
                onClick={clearPageError}
                type="button"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Registration Form */}
          <div className="form-section">
            <RegistrationForm
              onSubmit={handleSubmissionSuccess}
              isLoading={pageState.isSubmitting}
              className="register-form"
            />
          </div>

          {/* Additional Information */}
          <div className="info-section">
            <div className="info-card">
              <h3 className="info-title">What happens next?</h3>
              <ol className="info-list">
                <li>
                  Your registration will be reviewed by our administrators
                </li>
                <li>
                  You will receive an email notification with the decision
                </li>
                <li>
                  If approved, you'll receive login credentials to access the
                  system
                </li>
                <li>If rejected, you'll receive feedback on the reason</li>
              </ol>
            </div>

            <div className="info-card">
              <h3 className="info-title">Important Notes</h3>
              <ul className="info-list">
                <li>All fields marked with * are required</li>
                <li>Payment receipt must be under 5MB and in image format</li>
                <li>
                  Email addresses must be unique - you cannot register twice
                  with the same email
                </li>
                <li>
                  Please ensure all information is accurate as it cannot be
                  changed after submission
                </li>
              </ul>
              <br />
              <u>ማሳሰቢያ፡- አግልግሎት ለማግኘት ከታች የተዘረዘሩትን ነጥቦች ማሟላትዎን ያረጋግጡ።</u> <br />
              <ol>
                <li style={{ marginBottom: 10 }}>
                  በኢትዮጵያ ለሚኖሩ ዜጐች የአባልነት መመዝገበያ 1,000 ብር እና ሌላ ከውጭ የሚኖር ዲያስፖራ
                  አባል በትንሹ 1 ሰው ወይም በኢትዮጵያ የሚኖሩ የተመረጡ 20 ሰዎችን ሲያስገቡ አገልግሎት ማግኘት
                  ይችላሉ።
                </li>
                <li>
                  ከኢትዮጵያ ውጭ ነዋሪ የሆኑ ኢትዮጵያዊ ዜጐች የአባልነት መመዝገቢያ $100 እና ሌላ ከውጭ የሚኖር
                  ዲያስፖራ አባል በትንሹ 3 ሰው ሲያስገቡ አገልግሎት ማግኘት ይችላሉ።
                </li>
              </ol>
            </div>

            <div className="info-card">
              <h3 className="info-title">Bank Accounts</h3>
              <p className="info-text-2">For payers in Ethiopia:</p>
              Mirt sew Trading PlC
              <br />
              <br />
              <table className="footer-banks-table">
                <tr>
                  <td>
                    <p className="info-text">
                      250163851
                      <br />
                      Bank of Abysinia
                      <br />
                    </p>
                  </td>
                  <td>
                    <p className="info-text">
                      9900045801105
                      <br />
                      Amhara Bank
                      <br />
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className="info-text">
                      2310413074297016
                      <br />
                      Hibret Bank
                      <br />
                    </p>
                  </td>
                  <td>
                    <p className="info-text">
                      0020831334011
                      <br />
                      Dashen Bank
                      <br />
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className="info-text">
                      013041757778800
                      <br />
                      Awash Bank
                      <br />
                    </p>
                  </td>
                  <td>
                    <p className="info-text">
                      1932475200001
                      <br />
                      Oromia Bank
                      <br />
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className="info-text">
                      1000764196426
                      <br />
                      Commercial Bank of Ethiopia
                      <br />
                    </p>
                  </td>
                  <td>
                    <p className="info-text">
                      1311024009104001
                      <br />
                      Addis Bank
                      <br />
                    </p>
                  </td>
                </tr>
              </table>
              <br />
              <br />
              <p className="info-text-2">
                If you are paying from Banks out of Ethiopia, please use the
                following bank details:
              </p>
              <hr />
              <p className="info-text">
                <b>JANAMORA INTERNATIONAL ORG INC</b>
                <br />
                325 WESTON RD, YORK, QN M6N4Z9
                <br />
                Account Number = 1987-047
                <br />
                Swift Code = BOFMCAM2 <br />
                Bank of Montreal
              </p>
              <br />
              <hr />
              <p className="info-text">
                Mirtsew Trading PLC
                <br />
                FCY Saving Account (USD) <br />
                9900048801467
                <br />
                Swift Code = AMHRETAA
                <br />
                Amhara Bank
              </p>
              <br />
              <hr />
              <p className="info-text">
                Mirtsew Trading PLC
                <br />
                USD Account = 1311024009224001
                <br />
                Swift Code = ABSCETAA
                <br />
                Addis Bank
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
